import React, { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    EnrollmentType,
    useExperiment
} from "@/utils/experiment/use-experiment";
import { wacRefreshQ4Fy26Experiment } from "@/utils/experiment/wacRefreshExperiment";

import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_SM,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_XL,
    WCL_GRID_MAX_WIDTH_XL
} from "./base";

import type { spaceTokenNumberValue } from "../../../utils";

type ResponsivePaddingOptions = {
    mobile?: spaceTokenNumberValue;
    tablet?: spaceTokenNumberValue;
    desktop?: spaceTokenNumberValue;
};

export type ColumnLayoutProps = PropsWithChildren<{
    layout:
        | "12"
        | "10"
        | "8"
        | "8-4"
        | "4-8"
        | "9-3"
        | "3-9"
        | "6-6"
        | "7-5"
        | "5-7"
        | "3-3-6"
        | "6-3-3"
        | "4-4-4"
        | "3-3-3-3"
        | "2-2-2-2-2-2"
        | "article"
        | "overlay";
    paddingTopv2?: spaceTokenNumberValue | ResponsivePaddingOptions;
    paddingBottomv2?: spaceTokenNumberValue | ResponsivePaddingOptions;
    verticalAlignment?: "normal" | "start" | "end" | "center";
    renderAsList?: "ol" | "ul";
}>;

const baseStyle = css({
    "display": "grid",
    "boxSizing": "border-box",
    "gap": "var(--cl-gap)",
    "width": "100%",
    "--cl-gap": "24px",
    "--width-without-gaps": "calc(100% - var(--cl-gap) * 11)", // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
    [media.above.md]: {
        "--cl-gap": "32px"
    },
    // Prevent child content from expanding grid columns (minWidth: 0 allows shrinking below content size)
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> *": {
        minWidth: 0
    }
});

const fullWidthStyles = css({
    marginInline: "auto",
    maxWidth: "100%",
    paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
    paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
    [media.only.sm]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`
    },
    [media.above.md]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
        maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
    }
});

const xlDesktopBreakpointStyles = css({
    [media.above.lg]: {
        paddingInlineStart: `${WCL_GRID_PADDING_XL}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_XL}px`,
        maxWidth: `${WCL_GRID_MAX_WIDTH_XL + WCL_GRID_PADDING_XL * 2}px`
    }
});

const layout10 = css({
    [media.above.sm]: {
        "gridTemplateColumns": "repeat(12, 1fr)",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> *": {
            gridColumn: "2 / span 10"
        }
    }
});

const layout8 = css({
    [media.above.sm]: {
        // Slots can be any element and placed dynamically via Uniform
        // Due to the way Uniform slots in content (with extra script elements) we have to be a bit more mathematical
        // to get the intended grid alignment for asymmetrical layouts
        "gridTemplateColumns": "repeat(12, 1fr)",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> *": {
            gridColumn: "3 / span 8"
        }
    }
});

// For 8-4 the following is equivalent to having a 12 column grid
// where the first child is `gridColumn: 1 / span 8` and the second is `9 / -1`
const layout84 = css({
    [media.above.sm]: {
        "--total-gap-4": "calc(var(--cl-gap) * 3)", // 3 gaps appear in a 4 column section
        "gridTemplateColumns":
            "1fr calc(var(--width-without-gaps) * 4 / 12 + var(--total-gap-4))"
    }
});

const layout48 = css({
    [media.above.sm]: {
        "--total-gap-4": "calc(var(--cl-gap) * 3)", // 3 gaps appear in a 4 column section
        "gridTemplateColumns":
            "calc(var(--width-without-gaps) * 4 / 12 + var(--total-gap-4)) 1fr"
    }
});

const layout93 = css({
    [media.above.sm]: {
        "--total-gap-3": "calc(var(--cl-gap) * 2)", // 2 gaps appear in a 3 column section
        "gridTemplateColumns":
            "1fr calc(var(--width-without-gaps) * 3 / 12 + var(--total-gap-3))"
    }
});

const layout39 = css({
    [media.above.sm]: {
        "--total-gap-3": "calc(var(--cl-gap) * 2)", // 2 gaps appear in a 3 column section
        "gridTemplateColumns":
            "calc(var(--width-without-gaps) * 3 / 12 + var(--total-gap-3)) 1fr"
    }
});

const layout66 = css({
    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr"
    }
});

const layout75 = css({
    [media.above.sm]: {
        "--total-gap-5": "calc(var(--cl-gap) * 4)", // 4 gaps appear in a 5 column section
        "gridTemplateColumns":
            "1fr calc(var(--width-without-gaps) * 5 / 12 + var(--total-gap-5))"
    }
});

const layout57 = css({
    [media.above.sm]: {
        "--total-gap-5": "calc(var(--cl-gap) * 4)", // 2 gaps appear in a 3 column section
        "gridTemplateColumns":
            "calc(var(--width-without-gaps) * 5 / 12 + var(--total-gap-5)) 1fr"
    }
});

const layout336 = css({
    [media.above.sm]: {
        "--total-gap-3": "calc(var(--cl-gap) * 2)", // 2 gaps appear in a 3 column section
        "gridTemplateColumns":
            "calc(var(--width-without-gaps) * 3 / 12 + var(--total-gap-3)) calc(var(--width-without-gaps) * 3 / 12 + var(--total-gap-3)) 1fr"
    }
});

const layout633 = css({
    [media.above.sm]: {
        "--total-gap-3": "calc(var(--cl-gap) * 2)", // 2 gaps appear in a 3 column section
        "gridTemplateColumns":
            "1fr calc(var(--width-without-gaps) * 3 / 12 + var(--total-gap-3)) calc(var(--width-without-gaps) * 3 / 12 + var(--total-gap-3))"
    }
});

const layout444 = css({
    [media.above.sm]: {
        gridTemplateColumns: "repeat(3, 1fr)"
    }
});

const layout3333 = css({
    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr"
    },
    [media.above.md]: {
        gridTemplateColumns: "repeat(4, 1fr)"
    }
});

const layout222222 = css({
    gridTemplateColumns: "1fr 1fr",
    [media.above.sm]: {
        gridTemplateColumns: "repeat(6, 1fr)"
    }
});

const layoutArticle = css({
    [media.above.md]: {
        "--total-gap-4": "calc(var(--cl-gap) * 3)", // 3 gaps appear in a 4 column section
        "gridTemplateColumns":
            "calc(var(--width-without-gaps) * 4 / 12 + var(--total-gap-4)) 1fr"
    }
});

const layoutOverlay = css({
    gridTemplateColumns: "repeat(12, 1fr)"
});

const resetListStyles = css({
    listStyle: "none outside none",
    paddingInlineStart: 0,
    marginBlock: 0,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    li: {
        display: "flex",
        padding: 0,
        margin: 0
    }
});

// Compiled styling requires complex set of choices
export const ColumnLayout: React.FC<ColumnLayoutProps> = ({
    layout = "12",
    paddingTopv2,
    paddingBottomv2,
    verticalAlignment = "normal",
    renderAsList,
    children
    // eslint-disable-next-line complexity
}) => {
    const paddingTopObject = {
        // @ts-ignore
        mobile: paddingTopv2?.mobile ?? paddingTopv2 ?? "0",
        tablet:
            // @ts-ignore
            paddingTopv2?.tablet ?? paddingTopv2?.mobile ?? paddingTopv2 ?? "0",
        desktop:
            // @ts-ignore
            paddingTopv2?.desktop ??
            // @ts-ignore
            paddingTopv2?.tablet ??
            // @ts-ignore
            paddingTopv2?.mobile ??
            paddingTopv2 ??
            "0"
    };
    const paddingBottomObject = {
        // @ts-ignore
        mobile: paddingBottomv2?.mobile ?? paddingBottomv2 ?? "0",
        tablet:
            // @ts-ignore
            paddingBottomv2?.tablet ??
            // @ts-ignore
            paddingBottomv2?.mobile ??
            paddingBottomv2 ??
            "0",
        desktop:
            // @ts-ignore
            paddingBottomv2?.desktop ??
            // @ts-ignore
            paddingBottomv2?.tablet ??
            // @ts-ignore
            paddingBottomv2?.mobile ??
            paddingBottomv2 ??
            "0"
    };

    const paddingStyles = css({
        paddingBlockStart: "var(--ds-space-" + paddingTopObject.mobile + ")",
        paddingBlockEnd: "var(--ds-space-" + paddingBottomObject.mobile + ")",
        [media.above.sm]: {
            paddingBlockStart:
                "var(--ds-space-" + paddingTopObject.tablet + ")",
            paddingBlockEnd:
                "var(--ds-space-" + paddingBottomObject.tablet + ")"
        },
        [media.above.md]: {
            paddingBlockStart:
                "var(--ds-space-" + paddingTopObject.desktop + ")",
            paddingBlockEnd:
                "var(--ds-space-" + paddingBottomObject.desktop + ")"
        }
    });

    const alignmentStyles = css({
        alignItems: verticalAlignment
    });

    const Container = renderAsList ?? "div";

    const checkWacRefresh = useExperiment(wacRefreshQ4Fy26Experiment);
    const isWacRefresh =
        checkWacRefresh.type === EnrollmentType.ENROLLED &&
        checkWacRefresh.value;

    return (
        <Container
            css={[
                baseStyle,
                renderAsList && resetListStyles,
                fullWidthStyles,
                isWacRefresh && xlDesktopBreakpointStyles,
                paddingStyles,
                alignmentStyles,
                layout === "10" && layout10,
                layout === "8" && layout8,
                layout === "8-4" && layout84,
                layout === "4-8" && layout48,
                layout === "9-3" && layout93,
                layout === "3-9" && layout39,
                layout === "7-5" && layout75,
                layout === "5-7" && layout57,
                layout === "6-6" && layout66,
                layout === "4-4-4" && layout444,
                layout === "3-3-3-3" && layout3333,
                layout === "2-2-2-2-2-2" && layout222222,
                layout === "6-3-3" && layout633,
                layout === "3-3-6" && layout336,
                layout === "article" && layoutArticle,
                layout === "overlay" && layoutOverlay
            ]}
        >
            {children}
        </Container>
    );
};

// intended for use within Column Layouts or other components that already have the 12 column grid defined
export const SubColumnLayout: React.FC<ColumnLayoutProps> = ({
    layout = "12",
    paddingTopv2,
    paddingBottomv2,
    verticalAlignment = "normal",
    renderAsList,
    children
    // eslint-disable-next-line complexity
}) => {
    const paddingTopObject = {
        // @ts-ignore
        mobile: paddingTopv2?.mobile ?? "0",
        // @ts-ignore
        tablet: paddingTopv2?.tablet ?? paddingTopv2?.mobile ?? "0",
        desktop:
            // @ts-ignore
            paddingTopv2?.desktop ??
            // @ts-ignore
            paddingTopv2?.tablet ??
            // @ts-ignore
            paddingTopv2?.mobile ??
            "0"
    };
    const paddingBottomObject = {
        // @ts-ignore
        mobile: paddingBottomv2?.mobile ?? "0",
        // @ts-ignore
        tablet: paddingBottomv2?.tablet ?? paddingBottomv2?.mobile ?? "0",
        // @ts-ignore
        desktop:
            // @ts-ignore
            paddingBottomv2?.desktop ??
            // @ts-ignore
            paddingBottomv2?.tablet ??
            // @ts-ignore
            paddingBottomv2?.mobile ??
            "0"
    };

    const paddingStyles = css({
        paddingBlockStart: "var(--ds-space-" + paddingTopObject.mobile + ")",
        paddingBlockEnd: "var(--ds-space-" + paddingBottomObject.mobile + ")",
        [media.above.sm]: {
            paddingBlockStart:
                "var(--ds-space-" + paddingTopObject.tablet + ")",
            paddingBlockEnd:
                "var(--ds-space-" + paddingBottomObject.tablet + ")"
        },
        [media.above.md]: {
            paddingBlockStart:
                "var(--ds-space-" + paddingTopObject.desktop + ")",
            paddingBlockEnd:
                "var(--ds-space-" + paddingBottomObject.desktop + ")"
        }
    });

    const alignmentStyles = css({
        alignItems: verticalAlignment
    });

    const Container = renderAsList ?? "div";

    return (
        <Container
            css={[
                baseStyle,
                paddingStyles,
                alignmentStyles,
                renderAsList && resetListStyles,
                layout === "10" && layout10,
                layout === "8" && layout8,
                layout === "8-4" && layout84,
                layout === "4-8" && layout48,
                layout === "9-3" && layout93,
                layout === "3-9" && layout39,
                layout === "7-5" && layout75,
                layout === "5-7" && layout57,
                layout === "6-6" && layout66,
                layout === "4-4-4" && layout444,
                layout === "3-3-3-3" && layout3333,
                layout === "2-2-2-2-2-2" && layout222222,
                layout === "6-3-3" && layout633,
                layout === "3-3-6" && layout336
            ]}
        >
            {children}
        </Container>
    );
};

// Both components are exported as named exports above
